/**
 * Pure, dependency-free helpers for the partner-membership React Query cache key.
 *
 * Kept in a separate module so they can be imported by tests (and by any future
 * non-hook consumers) without pulling in the full `useAuth` / `bugsnag` chain.
 *
 * Cache-key contract
 * ──────────────────
 * The key is scoped to the authenticated user ID so that one user's cached
 * membership result can never satisfy a different user's gate after a
 * sign-out / sign-in switch in the same browser tab.
 *
 *   User A (id=1)  → ["/api/partner/restaurants", "1"]
 *   User B (id=2)  → ["/api/partner/restaurants", "2"]
 *   Unauthenticated → ["/api/partner/restaurants", "anon"]
 */

export const PARTNER_RESTAURANTS_URL = "/api/partner/restaurants";

/**
 * Builds the React Query cache key for the partner-membership lookup.
 *
 * Accepts `string | number | null` because the `user.id` field on the auth
 * context may arrive as either type depending on the backend serializer;
 * both are stringified identically so the isolation contract is preserved.
 *
 * @param userId - The authenticated user's ID (string or number), or null when unauthenticated.
 */
export function buildPartnerMembershipQueryKey(
  userId: string | number | null
): readonly [string, string] {
  return [PARTNER_RESTAURANTS_URL, userId !== null ? String(userId) : "anon"];
}
